import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Collapse,
  FormControl,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Clear as ClearIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Launch as LaunchIcon,
  OpenInNew as OpenInNewIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import NewsService, {
  fetchKeywords,
  listOfAnalysts,
  upsertFeedback,
} from "../services/NewsService";
import { AxiosInstance } from "../services/Axios";

const MABNewsFeed = ({ standalone = false }) => {
  const theme = useTheme();

  const [newsData, setNewsData] = useState([]);
  const [allNewsData, setAllNewsData] = useState([]); // Store all data for tag dropdown
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [relevanceFilter, setRelevanceFilter] = useState("relevant");
  const [analystFilter, setAnalystFilter] = useState("");
  const [analysts, setAnalysts] = useState([]);
  const [analystsLoading, setAnalystsLoading] = useState(false);
  const [keywordsData, setKeywordsData] = useState([]); // Store keywords to get user assignments
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [feedbackLoading, setFeedbackLoading] = useState(new Set());
  const [feedbackSuccess, setFeedbackSuccess] = useState(new Set());
  const [existingFeedback, setExistingFeedback] = useState(new Map()); // headline_unique_id -> feedback value

  // Lazy loading state
  const [displayedItems, setDisplayedItems] = useState(50); // Number of items to display initially
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const ITEMS_PER_LOAD = 25; // Number of items to load each time

  // Load all news data for tag dropdown (only once)
  useEffect(() => {
    const loadAllNewsData = async () => {
      try {
        const data = await NewsService.getNewsData();
        setAllNewsData(data);
      } catch (err) {
        console.error("Error loading all news data:", err);
      }
    };

    loadAllNewsData();
  }, []);

  // Load analysts list
  useEffect(() => {
    const loadAnalysts = async () => {
      setAnalystsLoading(true);
      try {
        const data = await listOfAnalysts();
        const analystsList = data.analysts || [];
        setAnalysts(analystsList);
      } catch (err) {
        console.error("Error loading analysts:", err);
      } finally {
        setAnalystsLoading(false);
      }
    };

    loadAnalysts();
  }, []);

  // Load keywords data to get user assignments for filtering
  useEffect(() => {
    const loadKeywords = async () => {
      try {
        const data = await fetchKeywords();
        setKeywordsData(data || []);
      } catch (err) {
        console.error("Error loading keywords:", err);
      }
    };

    loadKeywords();
  }, []);

  // Debounce search term to avoid searching on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Wait 500ms after user stops typing

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Load news data with filters
  useEffect(() => {
    const loadNewsData = async () => {
      try {
        setLoading(true);

        // Build query parameters (no analyst filter - will filter on frontend)
        const params = new URLSearchParams();
        if (tagFilter) params.append("tag", tagFilter);
        if (relevanceFilter !== "all") {
          params.append("relevant", relevanceFilter === "relevant");
        }
        if (debouncedSearchTerm) params.append("search", debouncedSearchTerm);
        params.append("last_n_days", "2");

        const queryString = params.toString();
        const url = queryString
          ? `news-proxy/latest-results?${queryString}`
          : "news-proxy/latest-results";

        const response = await AxiosInstance.get(url);
        setNewsData(response.data);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Error loading news data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadNewsData();
  }, [tagFilter, relevanceFilter, debouncedSearchTerm]); // Use debouncedSearchTerm instead of searchTerm

  // Clear expanded groups when filters change
  useEffect(() => {
    setExpandedGroups(new Set());
  }, [searchTerm, tagFilter, relevanceFilter, analystFilter]);

  // Reset displayed items when filters change
  useEffect(() => {
    setDisplayedItems(50);
    setHasMoreData(true);
  }, [searchTerm, tagFilter, relevanceFilter]);

  // Get unique tags for filter dropdown
  const uniqueTags = [...new Set(allNewsData.map((item) => item.tag))].sort();

  // Helper function to get display headline (translated_headline with fallback to headline)
  const getDisplayHeadline = (item) => {
    return item?.translated_headline || item?.headline || "No headline";
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Extract domain from URL
  const getDomainFromUrl = (url) => {
    try {
      const domain = new URL(url).hostname;
      return domain.replace("www.", "");
    } catch {
      return "Invalid URL";
    }
  };

  // Generate consistent color for tags based on tag name
  const getTagColor = (tag) => {
    if (!tag) return theme.palette.primary.main;

    // Create a better hash from the tag name using multiple techniques
    let hash1 = 0;
    let hash2 = 0;

    for (let i = 0; i < tag.length; i++) {
      const char = tag.charCodeAt(i);
      // First hash function
      hash1 = (hash1 << 5) - hash1 + char;
      hash1 = hash1 & hash1; // Convert to 32-bit integer

      // Second hash function (different multiplier and position-based)
      hash2 = hash2 * 31 + char * (i + 1);
      hash2 = hash2 & hash2; // Convert to 32-bit integer
    }

    // Combine both hashes for better distribution (add instead of XOR)
    const combinedHash = Math.abs(hash1 + hash2);

    // Use modulo to get a positive number
    const colorIndex = combinedHash % 24;

    // Define an expanded palette of distinct colors (avoiding grey)
    const colors = [
      theme.palette.primary.main, // Blue
      theme.palette.secondary.main, // Purple
      theme.palette.success.main, // Green
      theme.palette.warning.main, // Orange
      theme.palette.error.main, // Red
      "#9C27B0", // Deep Purple
      "#FF5722", // Deep Orange
      "#795548", // Brown
      "#607D8B", // Blue Grey
      "#E91E63", // Pink
      "#00BCD4", // Cyan
      "#8BC34A", // Light Green
      "#3F51B5", // Indigo
      "#FF9800", // Amber
      "#4CAF50", // Material Green
      "#F44336", // Material Red
      "#2196F3", // Material Blue
      "#673AB7", // Deep Purple
      "#FF6F00", // Deep Orange
      "#5D4037", // Brown
      "#455A64", // Blue Grey
      "#C2185B", // Pink
      "#0097A7", // Teal
      "#689F38", // Light Green
    ];

    return colors[colorIndex];
  };

  // Filter data by analyst on frontend
  const filteredData = useMemo(() => {
    let data = newsData;

    // Apply analyst filter on frontend
    if (analystFilter) {
      const analystName =
        typeof analystFilter === "object" ? analystFilter.name : analystFilter;

      // Get all tags where users field contains this analyst
      const matchingTags = keywordsData
        .filter((keyword) => {
          const users = keyword.users || "";
          // Check if users field contains the analyst name (case-insensitive)
          return users.toLowerCase().includes(analystName.toLowerCase());
        })
        .map((keyword) => keyword.tag);

      // Filter news data to only include items with matching tags
      if (matchingTags.length > 0) {
        data = data.filter((item) => matchingTags.includes(item.tag));
      } else {
        // If no matching tags, return empty array
        data = [];
      }
    }

    return data;
  }, [newsData, analystFilter, keywordsData]);

  // Group the filtered data
  const processedData = useMemo(() => {
    const groups = {};
    const nonRelevantItems = [];

    filteredData.forEach((item) => {
      // Handle both boolean and string values for relevant field
      const isRelevant = item.relevant === true || item.relevant === "true";
      if (isRelevant) {
        // Group relevant headlines
        const groupKey = `${item.tag}-${item.group_id}`;
        if (!groups[groupKey]) {
          groups[groupKey] = {
            primary: null,
            secondary: [],
            tag: item.tag,
            group: item.group_id,
            isNonRelevant: false,
          };
        }

        // Handle both boolean and string values for show_headline field
        const shouldShowHeadline =
          item.show_headline === true || item.show_headline === "true";
        if (shouldShowHeadline) {
          groups[groupKey].primary = item;
        } else {
          groups[groupKey].secondary.push(item);
        }
      } else {
        // Non-relevant headlines as individual items
        nonRelevantItems.push({
          primary: item,
          secondary: [],
          tag: item.tag,
          group: item.group_id,
          isNonRelevant: true,
        });
      }
    });

    // Sort secondary headlines within each group by llm_response_date_time (newest first)
    Object.values(groups).forEach((group) => {
      group.secondary.sort((a, b) => {
        const dateA = new Date(a.llm_response_date_time);
        const dateB = new Date(b.llm_response_date_time);
        return dateB - dateA; // Descending order (newest first)
      });
    });

    const result = [...Object.values(groups), ...nonRelevantItems];

    // Sort the main result array by the primary item's llm_response_date_time (newest first)
    result.sort((a, b) => {
      const dateA = new Date(a.primary?.llm_response_date_time);
      const dateB = new Date(b.primary?.llm_response_date_time);
      return dateB - dateA; // Descending order (newest first)
    });

    return result;
  }, [filteredData]);

  // Get displayed data for lazy loading
  const displayedData = useMemo(() => {
    return processedData.slice(0, displayedItems);
  }, [processedData, displayedItems]);

  // Load more items function
  const loadMoreItems = useCallback(() => {
    if (loadingMore || !hasMoreData) return;

    setLoadingMore(true);

    // Simulate a small delay for better UX
    setTimeout(() => {
      setDisplayedItems((prev) => {
        const newCount = prev + ITEMS_PER_LOAD;
        const maxItems = processedData.length;

        if (newCount >= maxItems) {
          setHasMoreData(false);
          return maxItems;
        }

        return newCount;
      });
      setLoadingMore(false);
    }, 300);
  }, [loadingMore, hasMoreData, processedData.length]);

  // Scroll detection for lazy loading
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMoreData) return;

      const scrollContainer = document.querySelector("[data-scroll-container]");
      if (!scrollContainer) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Load more when user scrolls to 80% of the content
      if (scrollPercentage > 0.8) {
        loadMoreItems();
      }
    };

    const scrollContainer = document.querySelector("[data-scroll-container]");
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, [loadingMore, hasMoreData, loadMoreItems]);

  // Handle expand/collapse
  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  // Handle relevance feedback
  const handleRelevanceFeedback = async (item, isRelevant) => {
    // Check if required fields exist
    if (!item.headline_unique_id) {
      console.error(
        "Cannot submit feedback: headline_unique_id is missing",
        item,
      );
      return;
    }

    const feedbackKey = `${item.headline_unique_id}-${isRelevant}`;

    // Set loading state
    setFeedbackLoading((prev) => new Set([...prev, feedbackKey]));
    setFeedbackSuccess((prev) => {
      const newSet = new Set(prev);
      newSet.delete(feedbackKey);
      return newSet;
    });

    try {
      // Prepare feedback data
      const feedbackData = {
        tag: item.tag || "unknown",
        headline: getDisplayHeadline(item),
        url: item.url || "",
        headline_unique_id: item.headline_unique_id,
        feedback: isRelevant,
        user_id: "user_123", // You can get this from auth context or props
      };

      // Submit feedback to API (upsert - update if exists, create if not)
      await upsertFeedback(feedbackData);

      // Show success state
      setFeedbackSuccess((prev) => new Set([...prev, feedbackKey]));

      // Update existing feedback state
      setExistingFeedback((prev) => {
        const newMap = new Map(prev);
        newMap.set(item.headline_unique_id, isRelevant);
        return newMap;
      });

      // Update local state to reflect the feedback
      setNewsData((prevData) =>
        prevData.map((newsItem) =>
          newsItem.headline_unique_id === item.headline_unique_id
            ? { ...newsItem, relevant: isRelevant }
            : newsItem,
        ),
      );

      // Clear success state after 2 seconds
      setTimeout(() => {
        setFeedbackSuccess((prev) => {
          const newSet = new Set(prev);
          newSet.delete(feedbackKey);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      // You could add a toast notification here for error feedback
    } finally {
      // Clear loading state
      setFeedbackLoading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(feedbackKey);
        return newSet;
      });
    }
  };

  // Handle popup window launch
  const handleLaunchPopup = () => {
    // Get the current URL and modify it to open in popup mode
    const currentUrl = window.location.href;
    const popupUrl =
      currentUrl + (currentUrl.includes("?") ? "&" : "?") + "popup=true";

    const popupWindow = window.open(
      popupUrl,
      "MABNewsFeed",
      "width=1200,height=800,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no",
    );

    if (popupWindow) {
      // Focus the popup window
      popupWindow.focus();
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        sx={{ p: 2 }}
      >
        <Typography variant="h6">Loading news data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Filters */}
      <Paper
        elevation={1}
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 1.5,
          backgroundColor: theme.palette.background.paper,
          flexShrink: 0,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Tag</InputLabel>
              <Select
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                label="Tag"
                variant="outlined"
              >
                <MenuItem value="">All Tags</MenuItem>
                {uniqueTags.map((tag) => (
                  <MenuItem key={tag} value={tag}>
                    {tag}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {tagFilter && (
              <Button
                size="small"
                onClick={() => setTagFilter("")}
                sx={{
                  minWidth: "auto",
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  borderRadius: "50%",
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    color: "text.primary",
                  },
                }}
                title="Clear tag filter"
              >
                <ClearIcon fontSize="small" />
              </Button>
            )}
          </Box>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Relevance</InputLabel>
            <Select
              value={relevanceFilter}
              onChange={(e) => setRelevanceFilter(e.target.value)}
              label="Relevance"
              variant="outlined"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="relevant">Relevant</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Analyst</InputLabel>
            <Select
              value={analystFilter}
              onChange={(e) => setAnalystFilter(e.target.value)}
              label="Analyst"
              variant="outlined"
              disabled={analystsLoading}
            >
              <MenuItem value="">All Analysts</MenuItem>
              {analysts.map((analyst) => {
                const analystName =
                  typeof analyst === "object" ? analyst.name : analyst;
                const analystValue =
                  typeof analyst === "object" ? analyst : analyst;
                const analystKey =
                  typeof analyst === "object"
                    ? analyst.id || analyst.name
                    : analyst;
                return (
                  <MenuItem key={analystKey} value={analystValue}>
                    {analystName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <TextField
            placeholder="Search headlines or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200 }}
            size="small"
          />

          {!standalone && (
            <Button
              variant="contained"
              startIcon={<LaunchIcon />}
              onClick={handleLaunchPopup}
              sx={{
                minWidth: "auto",
                px: 2,
                py: 1,
                height: "40px",
                backgroundColor: theme.palette.success.main,
                "&:hover": {
                  backgroundColor: theme.palette.success.dark,
                },
              }}
            >
              Launch Full Screen
            </Button>
          )}
        </Box>
      </Paper>

      {/* News Items Table */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TableContainer
          component={Paper}
          data-scroll-container
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.grey[100],
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.grey[400],
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.grey[500],
              },
            },
          }}
        >
          {displayedData.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
              sx={{ p: 2 }}
            >
              <Typography variant="h6" color="text.secondary">
                {newsData.length === 0
                  ? "Loading news data..."
                  : "No news items found"}
              </Typography>
            </Box>
          ) : (
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      width: "60px",
                      maxWidth: "120px",
                      fontWeight: 600,
                      backgroundColor: theme.palette.grey[100],
                    }}
                  >
                    Tag
                  </TableCell>
                  <TableCell
                    sx={{
                      width: 0,
                      padding: 0,
                      border: "none",
                      display: "none",
                    }}
                  ></TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor: theme.palette.grey[100],
                    }}
                  >
                    Headline
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "170px",
                      fontWeight: 600,
                      backgroundColor: theme.palette.grey[100],
                    }}
                  >
                    Time (UTC)
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "150px",
                      fontWeight: 600,
                      backgroundColor: theme.palette.grey[100],
                    }}
                  >
                    Source
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "180px",
                      fontWeight: 600,
                      backgroundColor: theme.palette.grey[100],
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedData.map((group) => {
                  const groupKey = `${group.tag}-${group.group}`;
                  const isExpanded = expandedGroups.has(groupKey);
                  const hasSecondary = group.secondary.length > 0;
                  const isNonRelevant = group.isNonRelevant;

                  return (
                    <React.Fragment key={groupKey}>
                      {/* Primary Headline Row */}
                      {group.primary && (
                        <TableRow
                          sx={{
                            backgroundColor: isNonRelevant
                              ? theme.palette.grey[50]
                              : "inherit",
                            opacity: isNonRelevant ? 0.8 : 1,
                            borderLeft: `4px solid ${
                              isNonRelevant
                                ? theme.palette.grey[400]
                                : getTagColor(group.primary.tag)
                            }`,
                            "&:hover": {
                              backgroundColor: isNonRelevant
                                ? theme.palette.grey[100]
                                : theme.palette.action.hover,
                              opacity: 1,
                            },
                          }}
                        >
                          {/* Tag */}
                          <TableCell
                            sx={{
                              width: "60px",
                              maxWidth: "120px",
                              py: 0.5,
                              px: 1,
                              overflow: "hidden",
                            }}
                          >
                            <Chip
                              label={group.primary.tag}
                              size="small"
                              sx={{
                                backgroundColor: isNonRelevant
                                  ? theme.palette.grey[400]
                                  : getTagColor(group.primary.tag),
                                color: "white",
                                fontWeight: 600,
                                height: "26px",
                                fontSize: "0.85rem",
                                maxWidth: "100%",
                                "& .MuiChip-label": {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                  px: 0.5,
                                },
                              }}
                              title={group.primary.tag}
                            />
                          </TableCell>

                          {/* Expand/Collapse - Hidden */}
                          <TableCell
                            sx={{
                              width: 0,
                              padding: 0,
                              border: "none",
                              display: "none",
                            }}
                          ></TableCell>

                          {/* Headline */}
                          <TableCell
                            sx={{
                              py: 0.5,
                              px: 1,
                              cursor: hasSecondary ? "pointer" : "default",
                              maxWidth: 0, // Allows cell to shrink
                              overflow: "hidden",
                            }}
                            onClick={
                              hasSecondary
                                ? () => toggleGroup(groupKey)
                                : undefined
                            }
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              sx={{ minWidth: 0 }}
                            >
                              {hasSecondary && (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                  sx={{ flexShrink: 0 }}
                                >
                                  {isExpanded ? (
                                    <ExpandLessIcon fontSize="small" />
                                  ) : (
                                    <ExpandMoreIcon fontSize="small" />
                                  )}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: "24px",
                                      height: "24px",
                                      borderRadius: "50%",
                                      backgroundColor: getTagColor(
                                        group.primary.tag,
                                      ),
                                      color: "white",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {group.secondary.length}
                                  </Box>
                                </Box>
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "1.05rem",
                                  fontWeight: isNonRelevant ? 400 : 500,
                                  color: isNonRelevant
                                    ? theme.palette.grey[600]
                                    : group.primary.relevant
                                      ? "text.primary"
                                      : "text.secondary",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  flex: 1,
                                  minWidth: 0,
                                  "&:hover": hasSecondary
                                    ? {
                                        color: theme.palette.primary.main,
                                        textDecoration: "underline",
                                      }
                                    : {},
                                }}
                                title={getDisplayHeadline(group.primary)}
                              >
                                {getDisplayHeadline(group.primary)}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Time */}
                          <TableCell
                            sx={{
                              width: "170px",
                              whiteSpace: "nowrap",
                              py: 0.5,
                              px: 1,
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              color="text.secondary"
                            >
                              <ScheduleIcon fontSize="small" />
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "0.85rem" }}
                              >
                                {formatDate(
                                  group.primary.llm_response_date_time,
                                )}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Source */}
                          <TableCell sx={{ width: "150px", py: 0.5, px: 1 }}>
                            <Link
                              href={group.primary.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                textDecoration: "none",
                                color: theme.palette.primary.main,
                                fontSize: "0.85rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                "&:hover": {
                                  textDecoration: "underline",
                                },
                              }}
                              title={getDomainFromUrl(group.primary.url)}
                            >
                              <OpenInNewIcon
                                fontSize="small"
                                sx={{ flexShrink: 0 }}
                              />
                              <Box
                                component="span"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {getDomainFromUrl(group.primary.url)}
                              </Box>
                            </Link>
                          </TableCell>

                          {/* Actions */}
                          <TableCell sx={{ py: 0.5, px: 1 }}>
                            <ButtonGroup
                              size="small"
                              variant="outlined"
                              sx={{
                                "& .MuiButton-root": {
                                  fontSize: "0.7rem",
                                  minWidth: "70px",
                                  height: "28px",
                                  px: 1,
                                },
                              }}
                            >
                              <Button
                                onClick={() =>
                                  handleRelevanceFeedback(group.primary, true)
                                }
                                disabled={feedbackLoading.has(
                                  `${group.primary.headline_unique_id}-true`,
                                )}
                                color="success"
                                variant={
                                  feedbackSuccess.has(
                                    `${group.primary.headline_unique_id}-true`,
                                  ) ||
                                  existingFeedback.get(
                                    group.primary.headline_unique_id,
                                  ) === true
                                    ? "contained"
                                    : "outlined"
                                }
                                sx={{
                                  fontSize: "0.7rem",
                                  textTransform: "none",
                                }}
                              >
                                {feedbackLoading.has(
                                  `${group.primary.headline_unique_id}-true`,
                                )
                                  ? "..."
                                  : "Relevant"}
                              </Button>
                              <Button
                                onClick={() =>
                                  handleRelevanceFeedback(group.primary, false)
                                }
                                disabled={feedbackLoading.has(
                                  `${group.primary.headline_unique_id}-false`,
                                )}
                                color="error"
                                variant={
                                  feedbackSuccess.has(
                                    `${group.primary.headline_unique_id}-false`,
                                  ) ||
                                  existingFeedback.get(
                                    group.primary.headline_unique_id,
                                  ) === false
                                    ? "contained"
                                    : "outlined"
                                }
                                sx={{
                                  fontSize: "0.7rem",
                                  textTransform: "none",
                                }}
                              >
                                {feedbackLoading.has(
                                  `${group.primary.headline_unique_id}-false`,
                                )
                                  ? "..."
                                  : "Not Relevant"}
                              </Button>
                            </ButtonGroup>
                          </TableCell>
                        </TableRow>
                      )}

                      {/* Secondary Headlines (Collapsible) */}
                      {hasSecondary && (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            sx={{
                              py: 0,
                              borderBottom: "none",
                              backgroundColor: theme.palette.grey[25],
                            }}
                          >
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Table
                                size="small"
                                sx={{ backgroundColor: "transparent" }}
                              >
                                <TableBody>
                                  {group.secondary.map((item, index) => (
                                    <TableRow
                                      key={`${item.tag}-${group.group}-${index}`}
                                      sx={{
                                        backgroundColor: theme.palette.grey[25],
                                        borderLeft: `3px solid ${getTagColor(
                                          item.tag,
                                        )}`,
                                        "&:hover": {
                                          backgroundColor:
                                            theme.palette.grey[50],
                                        },
                                      }}
                                    >
                                      {/* Tag */}
                                      <TableCell
                                        sx={{
                                          width: "60px",
                                          maxWidth: "120px",
                                          py: 0.5,
                                          px: 1,
                                          overflow: "hidden",
                                        }}
                                      >
                                        <Chip
                                          label={item.tag}
                                          size="small"
                                          sx={{
                                            backgroundColor: getTagColor(
                                              item.tag,
                                            ),
                                            color: "white",
                                            fontWeight: 600,
                                            height: "24px",
                                            fontSize: "0.8rem",
                                            maxWidth: "100%",
                                            "& .MuiChip-label": {
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              maxWidth: "100%",
                                              px: 0.5,
                                            },
                                          }}
                                          title={item.tag}
                                        />
                                      </TableCell>

                                      {/* Expand (empty for secondary) - Hidden */}
                                      <TableCell
                                        sx={{
                                          width: 0,
                                          padding: 0,
                                          border: "none",
                                          display: "none",
                                        }}
                                      ></TableCell>

                                      {/* Headline */}
                                      <TableCell
                                        sx={{
                                          py: 0.5,
                                          px: 1,
                                          maxWidth: 0, // Allows cell to shrink
                                          overflow: "hidden",
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: "1.0rem",
                                            fontWeight: 400,
                                            color: item.relevant
                                              ? "text.primary"
                                              : "text.secondary",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          }}
                                          title={getDisplayHeadline(item)}
                                        >
                                          {getDisplayHeadline(item)}
                                        </Typography>
                                      </TableCell>

                                      {/* Time */}
                                      <TableCell
                                        sx={{
                                          width: "170px",
                                          whiteSpace: "nowrap",
                                          py: 0.5,
                                          px: 1,
                                        }}
                                      >
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          gap={0.5}
                                          color="text.secondary"
                                        >
                                          <ScheduleIcon fontSize="small" />
                                          <Typography
                                            variant="caption"
                                            sx={{ fontSize: "0.8rem" }}
                                          >
                                            {formatDate(
                                              item.llm_response_date_time,
                                            )}
                                          </Typography>
                                        </Box>
                                      </TableCell>

                                      {/* Source */}
                                      <TableCell
                                        sx={{ width: "150px", py: 0.5, px: 1 }}
                                      >
                                        <Link
                                          href={item.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            textDecoration: "none",
                                            color: theme.palette.primary.main,
                                            fontSize: "0.8rem",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            "&:hover": {
                                              textDecoration: "underline",
                                            },
                                          }}
                                          title={getDomainFromUrl(item.url)}
                                        >
                                          <OpenInNewIcon
                                            fontSize="small"
                                            sx={{ flexShrink: 0 }}
                                          />
                                          <Box
                                            component="span"
                                            sx={{
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {getDomainFromUrl(item.url)}
                                          </Box>
                                        </Link>
                                      </TableCell>

                                      {/* Actions */}
                                      <TableCell
                                        sx={{ width: "180px", py: 0.5, px: 1 }}
                                      >
                                        <ButtonGroup
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            "& .MuiButton-root": {
                                              fontSize: "0.65rem",
                                              minWidth: "65px",
                                              height: "24px",
                                              px: 0.75,
                                            },
                                          }}
                                        >
                                          <Button
                                            onClick={() =>
                                              handleRelevanceFeedback(
                                                item,
                                                true,
                                              )
                                            }
                                            disabled={feedbackLoading.has(
                                              `${item.headline_unique_id}-true`,
                                            )}
                                            color="success"
                                            variant={
                                              feedbackSuccess.has(
                                                `${item.headline_unique_id}-true`,
                                              ) ||
                                              existingFeedback.get(
                                                item.headline_unique_id,
                                              ) === true
                                                ? "contained"
                                                : "outlined"
                                            }
                                            sx={{
                                              fontSize: "0.65rem",
                                              textTransform: "none",
                                            }}
                                          >
                                            {feedbackLoading.has(
                                              `${item.headline_unique_id}-true`,
                                            )
                                              ? "..."
                                              : "Relevant"}
                                          </Button>
                                          <Button
                                            onClick={() =>
                                              handleRelevanceFeedback(
                                                item,
                                                false,
                                              )
                                            }
                                            disabled={feedbackLoading.has(
                                              `${item.headline_unique_id}-false`,
                                            )}
                                            color="error"
                                            variant={
                                              feedbackSuccess.has(
                                                `${item.headline_unique_id}-false`,
                                              ) ||
                                              existingFeedback.get(
                                                item.headline_unique_id,
                                              ) === false
                                                ? "contained"
                                                : "outlined"
                                            }
                                            sx={{
                                              fontSize: "0.65rem",
                                              textTransform: "none",
                                            }}
                                          >
                                            {feedbackLoading.has(
                                              `${item.headline_unique_id}-false`,
                                            )
                                              ? "..."
                                              : "Not Relevant"}
                                          </Button>
                                        </ButtonGroup>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {/* Load More / Loading Indicator */}
          {displayedData.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 2,
                px: 2,
              }}
            >
              {loadingMore ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body2">
                    Loading more headlines...
                  </Typography>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      border: `2px solid ${theme.palette.grey[300]}`,
                      borderTop: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                </Box>
              ) : hasMoreData ? (
                <Button
                  variant="outlined"
                  onClick={loadMoreItems}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Load More Headlines ({processedData.length - displayedItems}{" "}
                  remaining)
                </Button>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  All headlines loaded ({processedData.length} total)
                </Typography>
              )}
            </Box>
          )}
        </TableContainer>
      </Box>
    </Box>
  );
};

export default MABNewsFeed;
